import { ChangeEventHandler, FocusEventHandler, ReactNode, useEffect, useId, useRef } from 'react'
import { PrependParameters } from 'rsuite/esm/internals/types/utils'
import { Checkbox, Container, DateInput, DatePicker, Form, Panel } from 'rsuite'
import { JSONTree } from 'react-json-tree'
import { t } from 'i18next'
import { TValueLabel } from '../utils/types'
import Select from './Controls/Select'

export default function Format() {
    function replace(text: string, replacedKeyValue = []) {
        var newText = text

        Object.keys(replacedKeyValue).forEach((key: any) => {
            const replacer = new RegExp(`{${key}}`, 'g')
            const value = replacedKeyValue[key]
            newText = newText.replace(replacer, value)
        })

        return newText
    }

    return {
        replace,
        required(text: string) {
            return replace(t('{field} required'), { field: text } as never)
        },
    }
}

// -- types ---

type TOnOff = 'on' | 'off' | 'new-password'

export type TField = {
    name: string
    value?: string
    label: ReactNode
    accepter?: any
    autoFocus?: boolean
    onFocus?: (FocusEventHandler<HTMLFormElement> & FocusEventHandler<HTMLInputElement>) | undefined
    onChange?:
    | (((value: any, event: React.SyntheticEvent) => void) &
        PrependParameters<ChangeEventHandler<HTMLInputElement>, [value: string]>)
    | undefined
    placeholder?: string
    readOnly?: boolean
    disabled?: boolean
    autoComplete?: TOnOff
    fullWidth?: boolean
}
type TStaticField = {
    name?: string,
    value?: string
    label: ReactNode
    type?: 'disabled' | 'readonly',
    placeholder?: string
    fullWidth?: boolean
}
type THiddenField = TField & { value?: string }
type TUploadField = TField & { acceptedFiles?: string }
type TPasswordField = TField & { checkStrength?: boolean }
type TSelectField = TField & { data: TValueLabel[]; placeholder?: string }
type TDateField = TField & { value?: Date | null }
type TCheckboxField = TField & { checkboxPosition?: 'left' | 'right'; defaultChecked: boolean }
type TView = { formValue: any; formError: any }

// --- styles ---

const inputStyle = {
    borderRadius: 0,
    borderSize: '2px',
    caretColor: 'var(--rs-input-caret-color)',
}
const selectStyle = {
    background: 'none',
    border: 'none',
    outline: 'none',
    padding: '7px',
    margin: '-7px',
}

// --- helper ---

const atLeastMinimumLength = (password: string) => new RegExp(/(?=.{8,})/).test(password)
const atLeastOneUppercaseLetter = (password: string) => new RegExp(/(?=.*?[A-Z])/).test(password)
const atLeastOneLowercaseLetter = (password: string) => new RegExp(/(?=.*?[a-z])/).test(password)
const atLeastOneNumber = (password: string) => new RegExp(/(?=.*?[0-9])/).test(password)
const atLeastOneSpecialChar = (password: string) => new RegExp(/(?=.*?[#?!@$ %^&*-_.,;:])/).test(password)

enum EPasswordStrength {
    STRONG,
    MEDIUM,
    WEAK,
    NONE,
}
const checkPasswordStrength = (password: string): EPasswordStrength => {
    if (!password) return EPasswordStrength.NONE

    let points = 0
    if (atLeastMinimumLength(password)) points++
    if (atLeastOneUppercaseLetter(password)) points++
    if (atLeastOneLowercaseLetter(password)) points++
    if (atLeastOneNumber(password)) points++
    if (atLeastOneSpecialChar(password)) points++
    if (points >= 5) return EPasswordStrength.STRONG
    if (points >= 3) return EPasswordStrength.MEDIUM
    return EPasswordStrength.WEAK
}

// --- the dorm controls ---

export const HiddenField = ({ name, value }: THiddenField) => {
    const id = useId()

    return <input type="hidden" name={name} value={value} />
}

export const StaticField = ({ name = '', value, label, type = 'disabled', ...rest }: TStaticField) => {
    const id = useId()
    const isDisabled = type === 'disabled'
    const isReadOnly = type === 'readonly'

    return (
        <Form.Group controlId={id}>
            <Form.Label>{label} </Form.Label>
            <Form.Control
                id={id}
                name={name || id}
                value={value}
                style={inputStyle}
                disabled={isDisabled}
                readOnly={isReadOnly}
                {...rest}
            />
            {/* <Input ref={ref} name={name} accepter={accepter} {...rest} /> */}
        </Form.Group>
    )
}

export const TextField = ({ name, label, accepter, autoComplete, autoFocus, onFocus, onChange, ...rest }: TField) => {
    const id = useId()

    return (
        <Form.Group controlId={name}>
            <Form.Label>{label} </Form.Label>
            <Form.Control
                id={id}
                name={name}
                accepter={accepter}
                autoComplete={autoComplete}
                autoFocus={autoFocus}
                onFocus={onFocus}
                onChange={onChange}
                style={inputStyle}
                {...rest}
            />
            {/* <Input ref={ref} name={name} accepter={accepter} {...rest} /> */}
        </Form.Group>
    )
}

export const PasswordField = ({ name, label, accepter, checkStrength = false, ...rest }: TPasswordField) => {
    const strength = useRef(EPasswordStrength.NONE)
    const id = useId()

    const onChange = (value: string) => {
        strength.current = checkPasswordStrength(value)
    }

    return (
        <Form.Group controlId={name}>
            <Form.Label>
                {label}
                {checkStrength && strength.current !== EPasswordStrength.NONE && (
                    <span className="strength-meter">
                        {strength.current === EPasswordStrength.STRONG && t('Strong')}
                        {strength.current === EPasswordStrength.MEDIUM && t('Medium')}
                        {strength.current === EPasswordStrength.WEAK && t('Weak')}
                    </span>
                )}
            </Form.Label>
            <Form.Control
                id={id}
                type="password"
                autoComplete="new-password"
                name={name}
                accepter={accepter}
                style={inputStyle}
                {...rest}
            />
            {/* <Input type="password" autoComplete="off" ref={ref} name={name} accepter={accepter} {...rest} /> */}
        </Form.Group>
    )
}

export const EmailField = ({ name, label, accepter, ...rest }: TField) => {
    const id = useId()

    return (
        <Form.Group controlId={name}>
            <Form.Label>{label} </Form.Label>
            <Form.Control id={id} type="email" name={name} accepter={accepter} {...rest} />
        </Form.Group>
    )
}

export const CheckboxField = ({
    name,
    label,
    accepter,
    defaultChecked,
    checkboxPosition = 'left',
    ...rest
}: TCheckboxField) => {
    const id = useId()

    return (
        <>
            {/* <Form.Group controlId={name} className="checkbox">
      {checkboxPosition == 'left' && <Form.Control type="checkbox" id={id} name={name} value={value} accepter={accepter} autoComplete={autoComplete} style={inputStyle} {...rest} />}
      <Form.Label>{label} for={id}</Form.Label>
      {checkboxPosition == 'right' && <Form.Control type="checkbox" id={id} name={name} value={value} accepter={accepter} autoComplete={autoComplete} style={inputStyle} {...rest} />}
    </Form.Group> */}

            <Form.Group controlId={name} className="checkbox">
                <Checkbox
                    name={name}
                    className="checkbox"
                    defaultChecked={defaultChecked}
                //onChange={(value: ValueType | undefined, checked: boolean) => setFormData({ ...formData, emptyNull: checked })}
                >
                    {label}
                </Checkbox>
            </Form.Group>
        </>
    )
}

export const DateField = ({ name, label, accepter, ...rest }: TDateField) => {
    const id = useId()
    //const format = "dd.MM.yyyy"
    //const format = "yyyy-MM-dd"
    const format = undefined

    return (
        <>
            <Form.Group controlId={name}>
                <Form.Label>{label}</Form.Label>
                <Form.Control
                    name={name}
                    accepter={rest.readOnly ? DateInput : DatePicker}
                    format={format}
                    //oneTap
                    {...rest}
                />

                {/* {rest.readOnly
          ? <DateInput id={id} name={name} format={format} {...rest} />
          : <DatePicker id={id} name={name} format={format} {...rest} />
        } */}
            </Form.Group>
        </>
    )
}

/**
 * data: list of label/value pair
 */
export const SelectField = ({ name, label, data, placeholder = '', ...rest }: TSelectField) => {
    const id = useId()

    return (
        <Form.Group controlId={name}>
            <Form.Label>{label}</Form.Label>
            <Form.Control id={id} name={name} accepter={Select} data={data} {...rest} />
        </Form.Group>
    )
}

export const UploadField = ({ name, label, accepter, autoComplete, acceptedFiles, ...rest }: TUploadField) => {
    const id = useId()

    return (
        <Form.Group controlId={name}>
            <Form.Label>{label} </Form.Label>
            <Form.Control
                id={id}
                type="file"
                name={name}
                accepter={accepter}
                autoComplete={autoComplete}
                style={inputStyle}
                {...rest}
            />
        </Form.Group>
    )
}

export const DisableOverlay = ({
    disabled,
    children,
    allowSelector = '[data-editlock-allow]',
    blurActiveOnDisable = true,
    banner,
    bannerPlacement = 'top',
}: {
    disabled: boolean
    children: ReactNode
    allowSelector?: string
    blurActiveOnDisable?: boolean
    banner?: ReactNode
    bannerPlacement?: 'top' | 'bottom'
}) => {
    useEffect(() => {
        if (!disabled || !blurActiveOnDisable) return

        const el = document.activeElement as HTMLElement | null
        if (el && allowSelector && el.closest?.(allowSelector)) return
        el?.blur?.()
    }, [disabled, blurActiveOnDisable, allowSelector])

    const isAllowedTarget = (target: EventTarget | null) => {
        if (!allowSelector) return false
        const el = target as HTMLElement | null
        return Boolean(el?.closest?.(allowSelector))
    }

    const blockIfNotAllowed = (e: React.SyntheticEvent) => {
        if (!disabled) return
        if (isAllowedTarget(e.target)) return
        e.preventDefault()
        e.stopPropagation()
    }

    const Banner = disabled && banner ? banner : null

    return (
        <div
            className={disabled ? 'editlock-disabled' : undefined}
            style={{ position: 'relative' }}
            onPointerDownCapture={blockIfNotAllowed}
            onPointerUpCapture={blockIfNotAllowed}
            onClickCapture={blockIfNotAllowed}
            onDoubleClickCapture={blockIfNotAllowed}
            onContextMenuCapture={blockIfNotAllowed}
            onKeyDownCapture={blockIfNotAllowed}
            onKeyUpCapture={blockIfNotAllowed}
            onPasteCapture={blockIfNotAllowed}
        >
            {bannerPlacement === 'top' && Banner}
            {children}
            {bannerPlacement === 'bottom' && Banner}
        </div>
    )
}

export const JSONView = ({ formValue, formError }: TView) => {
    return (
        <Container style={{ marginTop: '1rem', backgroundColor: 'var(--color-2)', border: '1px solid var(--color-3)' }}>
            {formValue && (
                <Panel className="json-tree-wrapper" header={t('Form values')}>
                    <JSONTree data={formValue || null} />
                </Panel>
            )}
            {formError && (
                <Panel className="json-tree-wrapper" header={t('Form errors')}>
                    <JSONTree data={formError || null} />
                </Panel>
            )}
        </Container>
    )
}
